import { useId, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, FileArchive, Loader2, X } from "lucide-react";
import type { GoogleImportPreview, GoogleImportRequest } from "@ckgo2/shared";
import {
  defaultLocationKey,
  readTakeoutExport,
  toImportRequest,
  type TakeoutReadError,
  type TakeoutReadResult,
} from "@ckgo2/shared/takeout";
import ErrorBanner from "@/components/common/ErrorBanner";
import ImportPreviewSummary from "./ImportPreviewSummary";
import {
  confirmGoogleImport,
  googleReviewsKeys,
  previewGoogleImport,
  useGoogleReviewSummary,
} from "./googleReviewsApi";

type TakeoutOk = Extract<TakeoutReadResult, { ok: true }>;
type FileError = TakeoutReadError | "unexpected";

/**
 * Tok nahrání jako jeden rozlišený stav, ne sada příznaků. Přečtený export (`result`)
 * a požadavek (`request`) žijí JEN tady: nejdou do klíče dotazu, `localStorage` ani logu
 * (jména a texty recenzí jsou osobní údaje cizích lidí, F2.1).
 *
 * `chooseLocation.selected` = volba, kterou admin udělal sám; dokud žádnou neudělal,
 * platí předvolba odvozená při vykreslení (`defaultLocationKey` se zapamatovanou lokací).
 * `preview` drží i výsledek náhledu (nebo jeho chybu): odpověď se zapíše jen k požadavku,
 * který je ve stavu, takže pozdní odpověď po „Zrušit" se neukáže (stejné pravidlo jako `done`).
 * Pro `done` stav drží i požadavek, protože souhrn výsledku ukazuje zdroj data exportu.
 */
type UploadState =
  | { step: "pick" }
  | { step: "reading" }
  | { step: "fileError"; error: FileError; fileName: string | null }
  | { step: "chooseLocation"; result: TakeoutOk; selected: string | null }
  | {
      step: "preview";
      request: GoogleImportRequest;
      preview: GoogleImportPreview | null;
      error: Error | null;
    }
  | { step: "done"; request: GoogleImportRequest; result: GoogleImportPreview };

interface TakeoutUploadPanelProps {
  onClose: () => void;
}

/**
 * Nahrání exportu Google Takeout (U6, M1): přečíst ZIP v prohlížeči → (víc lokací) výběr →
 * souhrn z náhledu API → „Potvrdit a uložit" / „Zrušit". Zapisuje JEN „Potvrdit a uložit"
 * a potvrzuje přesně ten požadavek, jehož souhrn admin viděl. Nepoužitelný soubor na API
 * nic nepošle. Panel vykresluje jen role z `GOOGLE_IMPORT_ROLES` (rozhoduje stránka).
 *
 * Požadavky jdou z obsluh událostí, nikdy z efektu: admin běží v `React.StrictMode`
 * a efekt při připojení by ve vývoji odeslal dva náhledy.
 */
export default function TakeoutUploadPanel({ onClose }: TakeoutUploadPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [state, setState] = useState<UploadState>({ step: "pick" });

  // Zapamatovaná lokace = lokace posledního potvrzeného exportu (F2.5); tatáž keš jako
  // blok souhrnu v Nastavení → Obsah webu. Předvolba se počítá až po dotazu (úspěch
  // i chyba), aby se nepředvybrala jiná lokace, než kterou dotaz vrátí.
  const summaryQuery = useGoogleReviewSummary();
  const remembered = summaryQuery.data?.exported?.locationKey ?? null;

  // Pořadí čtení exportu: „Zrušit" během čtení číslo posune a doběhlé čtení se zahodí
  // (jinak by po zavření panelu odešel náhled s jmény a texty recenzí, AK25).
  const readRun = useRef(0);

  const previewMutation = useMutation({ mutationFn: previewGoogleImport });
  // Obnova seznamu a souhrnu patří k mutaci, ne k volání `mutate`: callback volání se po
  // odpojení panelu nezavolá, zápis na serveru ale doběhne.
  const confirmMutation = useMutation({
    mutationFn: confirmGoogleImport,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: googleReviewsKeys.all }),
  });

  const startPreview = (request: GoogleImportRequest) => {
    setState({ step: "preview", request, preview: null, error: null });
    confirmMutation.reset();
    // Odpověď patří jen k požadavku, který je ve stavu (identita objektu).
    const settle = (patch: { preview: GoogleImportPreview } | { error: Error }) =>
      setState((current) =>
        current.step === "preview" && current.request === request
          ? { ...current, ...patch }
          : current,
      );
    previewMutation.mutate(request, {
      onSuccess: (preview) => settle({ preview }),
      onError: (error) => settle({ error }),
    });
  };

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const files = Array.from(input.files ?? []);
    if (files.length === 0) return;
    const run = ++readRun.current;
    setState({ step: "reading" });
    let result: TakeoutReadResult;
    try {
      result = await readTakeoutExport(files);
    } catch {
      // Čtečka vrací chyby jako hodnotu; výjimka je nečekaná (např. poškozený stream).
      if (readRun.current === run) {
        setState({ step: "fileError", error: "unexpected", fileName: files[0]?.name ?? null });
      }
      return;
    } finally {
      // Týž soubor jde vybrat znovu.
      input.value = "";
    }
    // „Zrušit" během čtení: přečtený export se zahodí a na API nic nejde.
    if (readRun.current !== run) return;
    if (!result.ok) {
      setState({ step: "fileError", error: result.error, fileName: result.fileName || null });
    } else if (result.locations.length === 1) {
      startPreview(toImportRequest(result, result.locations[0].locationKey));
    } else {
      setState({ step: "chooseLocation", result, selected: null });
    }
  };

  // Běžící potvrzení nejde vzít zpět: „Zrušit" je do jeho doběhnutí zakázané, aby admin
  // viděl výsledek zápisu (kartu výsledku nebo chybu).
  const confirming = confirmMutation.isPending;

  const handleCancel = () => {
    readRun.current += 1;
    previewMutation.reset();
    confirmMutation.reset();
    setState({ step: "pick" });
    onClose();
  };

  const handleConfirm = (request: GoogleImportRequest) => {
    confirmMutation.mutate(request, {
      onSuccess: (result) => {
        // Po „Zrušit" už ve stavu tentýž požadavek není: pozdní odpověď se nezobrazí.
        setState((current) =>
          current.step === "preview" && current.request === request
            ? { step: "done", request, result }
            : current,
        );
      },
    });
  };

  return (
    <div className="card space-y-4">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("content.googleReviews.upload.title")}
        </h2>
        <button
          type="button"
          onClick={handleCancel}
          disabled={confirming}
          className="btn-ghost btn-sm"
          aria-label={t("content.googleReviews.upload.cancel")}
          title={t("content.googleReviews.upload.cancel")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {state.step === "pick" && <PickStep onFiles={handleFiles} />}

      {state.step === "reading" && (
        <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("content.googleReviews.upload.reading")}
        </p>
      )}

      {state.step === "fileError" && (
        <FileErrorStep
          error={state.error}
          fileName={state.fileName}
          onPickAnother={() => setState({ step: "pick" })}
        />
      )}

      {state.step === "chooseLocation" && (
        <LocationStep
          result={state.result}
          selected={
            state.selected ??
            (summaryQuery.isFetched ? defaultLocationKey(state.result.locations, remembered) : null)
          }
          onSelect={(key) => setState({ ...state, selected: key })}
          onContinue={(key) => startPreview(toImportRequest(state.result, key))}
          onCancel={handleCancel}
        />
      )}

      {state.step === "preview" && (
        <PreviewStep
          request={state.request}
          preview={state.preview}
          previewError={state.error}
          confirmError={confirmMutation.error}
          confirming={confirming}
          onConfirm={() => handleConfirm(state.request)}
          onCancel={handleCancel}
        />
      )}

      {state.step === "done" && (
        <div className="space-y-4">
          <p className="text-success-700 dark:text-success-400 flex items-center gap-2 font-medium">
            <CheckCircle className="h-5 w-5" />
            {t("content.googleReviews.upload.doneTitle")}
          </p>
          <ImportPreviewSummary preview={state.result} request={state.request} mode="result" />
          <div className="flex justify-end">
            <button type="button" onClick={handleCancel} className="btn-primary">
              {t("content.googleReviews.upload.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PickStep({ onFiles }: { onFiles: (event: ChangeEvent<HTMLInputElement>) => void }) {
  const { t } = useTranslation();
  const inputId = useId();
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t("content.googleReviews.upload.intro")}
      </p>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {t("content.googleReviews.upload.fileLabel")}
      </label>
      <input
        id={inputId}
        type="file"
        accept=".zip,application/zip"
        multiple
        onChange={onFiles}
        className="block w-full text-sm text-gray-700 dark:text-gray-300"
      />
    </div>
  );
}

/** Nepoužitelný soubor: důvod + vždy táž věta, co admin očekával. Na API nejde nic. */
function FileErrorStep({
  error,
  fileName,
  onPickAnother,
}: {
  error: FileError;
  fileName: string | null;
  onPickAnother: () => void;
}) {
  const { t } = useTranslation();
  // Lokální mapa z literálních `t()` (typovaný `t()` dynamický klíč neumí).
  const reasons: Record<FileError, string> = {
    not_zip: t("content.googleReviews.upload.errorNotZip"),
    not_takeout: t("content.googleReviews.upload.errorNotTakeout"),
    unsupported_zip: t("content.googleReviews.upload.errorUnsupportedZip"),
    no_reviews: t("content.googleReviews.upload.errorNoReviews"),
    unexpected: t("content.googleReviews.upload.errorUnexpected"),
  };
  return (
    <div className="space-y-3">
      <div
        role="alert"
        className="bg-error-50 dark:bg-error-900/30 rounded-lg border border-red-200 p-4 text-sm dark:border-red-800/50"
      >
        <p className="text-error-700 dark:text-error-400 font-medium">{reasons[error]}</p>
        <p className="text-error-700 dark:text-error-400 mt-1">
          {t("content.googleReviews.upload.expected")}
        </p>
        {fileName && (
          <p className="mt-1 flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <FileArchive className="h-4 w-4" />
            {t("content.googleReviews.upload.fileName", { name: fileName })}
          </p>
        )}
      </div>
      <button type="button" onClick={onPickAnother} className="btn-secondary">
        {t("content.googleReviews.upload.pickAnother")}
      </button>
    </div>
  );
}

/** Výběr lokace (jen víc lokací). Náhled odejde až z „Pokračovat". */
function LocationStep({
  result,
  selected,
  onSelect,
  onContinue,
  onCancel,
}: {
  result: TakeoutOk;
  selected: string | null;
  onSelect: (locationKey: string) => void;
  onContinue: (locationKey: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium text-gray-900 dark:text-white">
        {t("content.googleReviews.upload.chooseLocationTitle")}
      </legend>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t("content.googleReviews.upload.chooseLocationHint")}
      </p>
      {result.locations.map((location) => (
        <label
          key={location.locationKey}
          className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
        >
          <input
            type="radio"
            name="takeout-location"
            value={location.locationKey}
            checked={selected === location.locationKey}
            onChange={() => onSelect(location.locationKey)}
            className="mt-1"
          />
          <span className="text-sm">
            <span className="block font-medium text-gray-900 dark:text-white">{location.name}</span>
            <span className="block break-all text-gray-500 dark:text-gray-400">
              {location.placeId ?? t("content.googleReviews.summary.placeIdMissing")}
            </span>
            <span className="block text-gray-500 dark:text-gray-400">
              {t("content.googleReviews.upload.locationReviews", {
                count: location.reviews.length,
              })}
            </span>
          </span>
        </label>
      ))}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-ghost">
          {t("content.googleReviews.upload.cancel")}
        </button>
        <button
          type="button"
          disabled={selected === null}
          onClick={() => {
            if (selected !== null) onContinue(selected);
          }}
          className="btn-primary"
        >
          {t("content.googleReviews.upload.continue")}
        </button>
      </div>
    </fieldset>
  );
}

/** Souhrn z náhledu (M1) a jediné místo, odkud jde potvrzení. */
function PreviewStep({
  request,
  preview,
  previewError,
  confirmError,
  confirming,
  onConfirm,
  onCancel,
}: {
  request: GoogleImportRequest;
  preview: GoogleImportPreview | null;
  previewError: Error | null;
  confirmError: Error | null;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {!preview && !previewError && (
        <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("content.googleReviews.upload.previewing")}
        </p>
      )}
      <ErrorBanner error={previewError} />
      {preview && (
        <>
          <h3 className="font-medium text-gray-900 dark:text-white">
            {t("content.googleReviews.upload.summaryTitle")}
          </h3>
          <ImportPreviewSummary preview={preview} request={request} mode="preview" />
        </>
      )}
      <ErrorBanner error={confirmError} />
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} disabled={confirming} className="btn-ghost">
          {t("content.googleReviews.upload.cancel")}
        </button>
        {preview && (
          <button type="button" disabled={confirming} onClick={onConfirm} className="btn-primary">
            {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("content.googleReviews.upload.confirm")}
          </button>
        )}
      </div>
    </div>
  );
}
